import classnames from 'classnames'
import * as React from 'react'
import * as Reakit from 'reakit/Composite'

import { BUTTON_DEFAULT_ELEMENT, Button, ButtonComponentProps, ButtonTheme } from '../button'
import { ListContext } from './hooks'

export interface ListItemProps {
  /**
   * Theme when the button is selected
   */
  activeTheme?: keyof typeof ButtonTheme

  /**
   * Close parent popover when the item is clicked
   */
  closeOnSelect?: boolean

  /**
   * OnChange callback when item is clicked or navigated to
   * @private
   */
  onSelectItem?: (item: string, closeOnSelect?: boolean) => void
}

export function ListItem<TagType extends React.ElementType = typeof BUTTON_DEFAULT_ELEMENT>({
  as,
  activeTheme = 'blue',
  onSelectItem,
  onClick,
  onKeyPress,
  className,
  closeOnSelect = true,
  children,
  ...props
}: React.PropsWithChildren<ListItemProps> & ButtonComponentProps<TagType>) {
  const state = React.useContext(ListContext)

  return (
    <Reakit.CompositeItem
      {...state}
      {...props}
      onClick={(ev) => {
        onClick?.(ev)
        onSelectItem?.(ev.currentTarget.id, closeOnSelect)
      }}
      onKeyPress={(ev) => {
        onKeyPress?.(ev)
        onSelectItem?.(ev.currentTarget.id, closeOnSelect)
      }}
    >
      {(itemProps: ButtonComponentProps<TagType>) => {
        const active = Boolean(itemProps['aria-selected'])

        return (
          <Button
            {...itemProps}
            as={as}
            role="listitem"
            type="button"
            align="left"
            theme={active ? activeTheme : 'menuItem'}
            contentClass="truncate font-normal"
            className={classnames('shrink-0', className)}
          >
            {children}
          </Button>
        )
      }}
    </Reakit.CompositeItem>
  )
}
